<template>
  <Teleport to="body">
    <Transition name="palette">
      <div
        v-if="palette.isOpen.value"
        class="kui:fixed kui:inset-0 kui:z-50 kui:flex kui:justify-center"
        @click.self="palette.close()"
      >
        <!-- Backdrop -->
        <div class="kui:absolute kui:inset-0 kui:bg-black/40" @click="palette.close()" />

        <!-- Palette Card -->
        <div
          class="kui:relative kui:w-full kui:max-w-lg kui:mt-[15vh] kui:mx-4 kui:h-fit kui:rounded-xl kui:bg-surface kui:shadow-lg kui:border kui:border-gray-03 kui:overflow-hidden"
          @click.stop
        >
          <!-- Search Input -->
          <div class="kui:flex kui:items-center kui:gap-3 kui:px-4 kui:py-3 kui:border-b kui:border-gray-03">
            <KuiIcon icon="search" size="20" class="kui:text-gray-04 kui:shrink-0" />
            <input
              ref="inputRef"
              v-model="palette.query.value"
              type="text"
              placeholder="Search commands, features, initiatives, stories..."
              class="kui:flex-1 kui:bg-transparent kui:text-gray-07 kui:text-base kui:md:text-sm kui:outline-none kui:placeholder-gray-04"
              @keydown="handleKeydown"
            />
            <kbd class="kui:hidden kui:sm:inline kui:text-[10px] kui:font-medium kui:text-gray-04 kui:bg-gray-02 kui:px-1.5 kui:py-0.5 kui:rounded kui:border kui:border-gray-03">
              ESC
            </kbd>
          </div>

          <!-- Results -->
          <div ref="resultsRef" class="kui:overflow-y-auto kui:max-h-[360px]">
            <template v-if="flatList.length > 0">
              <template v-for="group in grouped" :key="group.category">
                <!-- Category Header -->
                <div class="kui:px-4 kui:pt-3 kui:pb-1.5">
                  <span class="kui:text-[10px] kui:font-semibold kui:uppercase kui:tracking-wider kui:text-gray-04">
                    {{ group.label }}
                  </span>
                </div>

                <!-- Command Items -->
                <div
                  v-for="cmd in group.commands"
                  :key="cmd.id"
                  :data-index="flatIndexOf(cmd)"
                  class="kui:flex kui:items-center kui:gap-3 kui:px-4 kui:py-2 kui:cursor-pointer kui:transition-colors"
                  :class="selectedIndex === flatIndexOf(cmd)
                    ? 'kui:bg-kipu-purple/10 kui:text-kipu-purple'
                    : 'kui:text-gray-07 kui:hover:bg-gray-02'"
                  @click="palette.executeCommand(cmd)"
                  @mouseenter="selectedIndex = flatIndexOf(cmd)"
                >
                  <KuiIcon
                    v-if="cmd.icon"
                    :icon="cmd.icon"
                    size="18"
                    class="kui:shrink-0"
                    :class="selectedIndex === flatIndexOf(cmd) ? 'kui:text-kipu-purple' : 'kui:text-gray-05'"
                  />
                  <span class="kui:flex-1 kui:text-sm kui:truncate">{{ cmd.label }}</span>
                  <kbd
                    v-if="cmd.shortcut"
                    class="kui:text-[10px] kui:font-medium kui:text-gray-04 kui:bg-gray-02 kui:px-1.5 kui:py-0.5 kui:rounded kui:border kui:border-gray-03"
                  >
                    {{ cmd.shortcut }}
                  </kbd>
                </div>
              </template>
            </template>

            <!-- No Results -->
            <div v-else class="kui:px-4 kui:py-8 kui:text-center">
              <KuiIcon icon="search_off" size="28" class="kui:text-gray-03" />
              <p class="kui:text-sm kui:text-gray-04 kui:mt-2">No matching commands</p>
            </div>
          </div>

          <!-- Footer -->
          <div class="kui:flex kui:items-center kui:gap-4 kui:px-4 kui:py-2 kui:border-t kui:border-gray-03 kui:bg-gray-01/50">
            <span class="kui:text-[10px] kui:text-gray-04 kui:flex kui:items-center kui:gap-1">
              <kbd class="kui:font-medium kui:bg-gray-02 kui:px-1 kui:rounded kui:border kui:border-gray-03">↑↓</kbd>
              navigate
            </span>
            <span class="kui:text-[10px] kui:text-gray-04 kui:flex kui:items-center kui:gap-1">
              <kbd class="kui:font-medium kui:bg-gray-02 kui:px-1 kui:rounded kui:border kui:border-gray-03">↵</kbd>
              select
            </span>
            <span class="kui:text-[10px] kui:text-gray-04 kui:flex kui:items-center kui:gap-1">
              <kbd class="kui:font-medium kui:bg-gray-02 kui:px-1 kui:rounded kui:border kui:border-gray-03">esc</kbd>
              close
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useCommandPalette } from '@/composables/useCommandPalette'
import type { PaletteCommand } from '@/composables/useCommandPalette'

const palette = useCommandPalette()
const inputRef = ref<HTMLInputElement | null>(null)
const resultsRef = ref<HTMLElement | null>(null)
const selectedIndex = ref(0)

// Group commands by category
const grouped = computed(() => {
  const groups: { category: string; label: string; commands: PaletteCommand[] }[] = []
  const categoryOrder: Array<PaletteCommand['category']> = ['navigation', 'content', 'action']
  const categoryLabels: Record<string, string> = {
    navigation: 'Navigation',
    content: 'Content',
    action: 'Actions'
  }

  for (const cat of categoryOrder) {
    const cmds = palette.filteredCommands.value.filter(c => c.category === cat)
    if (cmds.length > 0) {
      groups.push({ category: cat, label: categoryLabels[cat], commands: cmds })
    }
  }
  return groups
})

// Flat list for index-based keyboard navigation
const flatList = computed(() => {
  const list: PaletteCommand[] = []
  for (const group of grouped.value) {
    list.push(...group.commands)
  }
  return list
})

function flatIndexOf(cmd: PaletteCommand): number {
  return flatList.value.findIndex(c => c.id === cmd.id)
}

// Reset selection when query changes
watch(() => palette.query.value, () => {
  selectedIndex.value = 0
})

// Auto-focus input when opened
watch(() => palette.isOpen.value, (open) => {
  if (open) {
    selectedIndex.value = 0
    nextTick(() => inputRef.value?.focus())
  }
})

// Scroll selected item into view
watch(selectedIndex, (index) => {
  nextTick(() => {
    const el = resultsRef.value?.querySelector(`[data-index="${index}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  })
})

function handleKeydown(e: KeyboardEvent) {
  const total = flatList.value.length
  if (total === 0) return

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      selectedIndex.value = (selectedIndex.value + 1) % total
      break
    case 'ArrowUp':
      e.preventDefault()
      selectedIndex.value = (selectedIndex.value - 1 + total) % total
      break
    case 'Enter':
      e.preventDefault()
      if (flatList.value[selectedIndex.value]) {
        palette.executeCommand(flatList.value[selectedIndex.value])
      }
      break
    case 'Escape':
      e.preventDefault()
      palette.close()
      break
  }
}
</script>

<style scoped>
.palette-enter-active,
.palette-leave-active {
  transition: opacity 0.15s ease;
}
.palette-enter-active > :nth-child(2),
.palette-leave-active > :nth-child(2) {
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.palette-enter-from,
.palette-leave-to {
  opacity: 0;
}
.palette-enter-from > :nth-child(2) {
  transform: scale(0.95);
  opacity: 0;
}
.palette-leave-to > :nth-child(2) {
  transform: scale(0.95);
  opacity: 0;
}
</style>
